import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import "./parent.css";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime12(value) {
  if (!value) return "";
  const text = String(value);
  const [hStr, mStr] = text.split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return text;
  const ampm = hour >= 12 ? "PM" : "AM";
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${(mStr || "00").slice(0, 2)} ${ampm}`;
}

function formatRange(entry) {
  if (!entry?.start_time && !entry?.end_time) return `Period ${entry?.period_number || ""}`;
  return `${formatTime12(entry.start_time)} – ${formatTime12(entry.end_time)}`;
}

function ParentChildTimetable() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/parents/me/children/${studentId}/timetable`,
          { headers: getAuthHeaders() },
        );
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res.ok) throw new Error("Failed to load timetable");
        const data = await res.json();
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentId]);

  const { days, periods, grid } = useMemo(() => {
    const daySet = new Set(rows.map((r) => r.day_of_week));
    const daysList = DAY_ORDER.filter((d) => daySet.has(d));
    const periodNums = [...new Set(rows.map((r) => r.period_number))].sort((a, b) => a - b);
    const map = {};
    rows.forEach((r) => {
      map[`${r.day_of_week}-${r.period_number}`] = r;
    });
    return { days: daysList.length ? daysList : DAY_ORDER.slice(0, 5), periods: periodNums, grid: map };
  }, [rows]);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading timetable…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <div className="pp-detail-top">
        <button
          type="button"
          className="pp-btn pp-btn--ghost pp-btn--icon"
          aria-label="Back"
          onClick={() => navigate(`/parent/children/${studentId}`)}
        >
          ←
        </button>
        <div>
          <h1>Class timetable</h1>
          <p className="pp-muted">Weekly schedule for your child&apos;s class.</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="pp-empty">No timetable published for this class yet.</div>
      ) : (
        <div className="pp-timetable-wrap">
          <table className="pp-timetable">
            <thead>
              <tr>
                <th>Period</th>
                {days.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p}>
                  <td className="pp-timetable-period">
                    <strong>P{p}</strong>
                  </td>
                  {days.map((d) => {
                    const entry = grid[`${d}-${p}`];
                    return (
                      <td key={`${d}-${p}`} className={entry ? "pp-timetable-cell--filled" : ""}>
                        {entry ? (
                          <div className="pp-timetable-slot">
                            <strong>{entry.course_name || "Subject"}</strong>
                            <span>{entry.teacher_name || "—"}</span>
                            <small>{formatRange(entry)}</small>
                            {entry.room ? <small className="pp-timetable-room">Room {entry.room}</small> : null}
                          </div>
                        ) : (
                          <span className="pp-muted">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ParentChildTimetable;
